import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { CaseStatusData } from '../../../types/dashboard';
import { useLocalization } from '../../../contexts/LocalizationContext';

interface CaseStatusDistributionProps {
  data: CaseStatusData[];
  height?: number;
  showLegend?: boolean;
  className?: string;
}

const CaseStatusDistribution: React.FC<CaseStatusDistributionProps> = ({ 
  data, 
  height = 300, 
  showLegend = true,
  className = ''
}) => {
  const { strings } = useLocalization();
  const COLORS = {
    active: '#1A2B4C',
    pending: '#C9A14B',
    closed: '#6B7280',
    won: '#2E7D32',
    lost: '#D32F2F'
  };

  const getStatusColor = (status: string) => {
    return COLORS[status as keyof typeof COLORS] || '#6B7280';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      active: strings.dashboard.statusActive,
      pending: strings.dashboard.statusPending,
      closed: strings.dashboard.statusClosed,
      won: strings.dashboard.statusWon,
      lost: strings.dashboard.statusLost
    };
    return labels[status as keyof typeof labels] || status;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length && payload[0] && payload[0].payload) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700">
          <div className="flex items-center space-x-2 mb-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: data.fill }}
            ></div>
            <span className="text-sm font-medium text-slate-800 dark:text-white">
              {getStatusLabel(data.status)}
            </span>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600 dark:text-slate-300">Count:</span>
              <span className="text-sm font-semibold text-slate-800 dark:text-white">
                {data.count || 0}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600 dark:text-slate-300">Percentage:</span>
              <span className="text-sm font-semibold text-slate-800 dark:text-white">
                {(data.percentage || 0).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600 dark:text-slate-300">Revenue:</span>
              <span className="text-sm font-semibold text-slate-800 dark:text-white">
                {formatCurrency(data.revenue || 0)}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }: any) => {
    return (
      <div className="flex flex-wrap justify-center gap-4 mt-4">
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center space-x-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            ></div>
            <span className="text-sm text-slate-600 dark:text-slate-300">
              {getStatusLabel(entry.value)}
            </span>
            <span className="text-sm font-semibold text-slate-800 dark:text-white">
              ({entry.payload.count})
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    if (percent < 0.05) return null; // Don't show labels for slices less than 5%
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Transform data to match Recharts expected format
  const chartData = data.map(item => ({
    ...item,
    name: getStatusLabel(item.status),
    fill: getStatusColor(item.status)
  }));

  return (
    <div className={`w-full ${className}`}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={80}
            fill="#8884d8"
            dataKey="count"
          >
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.fill} 
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          {showLegend && <Legend content={<CustomLegend />} />}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CaseStatusDistribution;
